import { assistantApi, type AssistantSession } from '@shared/api-client';
import { useChat } from '@ai-sdk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DefaultChatTransport, type UIMessage } from 'ai';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

type PageContext = Record<string, string>;

type AssistantContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  messages: UIMessage[];
  status: 'ready' | 'submitted' | 'streaming' | 'error';
  error: Error | undefined;
  sessionId: string | null;
  sessions: AssistantSession[];
  isLoadingHistory: boolean;
  send: (text: string) => Promise<void>;
  stop: () => void;
  newChat: () => void;
  selectSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  pageContext: PageContext;
  setPageContext: (context: PageContext) => void;
};

const AssistantContext = React.createContext<AssistantContextValue | null>(
  null,
);

export const useAssistant = () => {
  const context = React.useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used inside AssistantProvider');
  }
  return context;
};

export const useAssistantPageContext = (context: PageContext) => {
  const { setPageContext } = useAssistant();
  const serialized = JSON.stringify(context);
  React.useEffect(() => {
    setPageContext(context);
    return () => setPageContext({});
    // The serialized value is intentionally the dependency: callers can pass a
    // fresh object without resetting context on every render.
  }, [serialized, setPageContext]);
};

export const AssistantProvider = ({
  projectId,
  children,
}: {
  projectId?: string;
  children: React.ReactNode;
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false);
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = React.useState(false);
  const [pageContext, setPageContext] = React.useState<PageContext>({});
  const sessionRef = React.useRef<string | null>(null);
  const pageContextRef = React.useRef<PageContext>({});
  const routePage = location.pathname.endsWith('/realtime')
    ? 'Realtime'
    : location.pathname.endsWith('/data-sources')
      ? 'Data sources'
      : location.pathname.endsWith('/details')
        ? 'Project details'
        : 'Overview';
  const resolvedPageContext = React.useMemo(
    () => ({
      ...(projectId ? { projectId } : {}),
      page: routePage,
      ...pageContext,
    }),
    [pageContext, projectId, routePage],
  );
  pageContextRef.current = resolvedPageContext;

  const sessionsQuery = useQuery({
    queryKey: ['assistant', 'sessions'],
    queryFn: assistantApi.listSessions,
    enabled: Boolean(projectId),
    staleTime: 15_000,
  });

  const transport = React.useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        prepareSendMessagesRequest: async ({ messages }) => {
          const credentials = await assistantApi.getStreamCredentials();
          return {
            api: credentials.streamUrl,
            headers: { Authorization: `Bearer ${credentials.token}` },
            body: {
              sessionId: sessionRef.current,
              messages: messages.slice(-1),
              pageContext: {
                path: location.pathname,
                ...pageContextRef.current,
              },
            },
          };
        },
      }),
    [location.pathname],
  );

  const { messages, status, error, sendMessage, setMessages, stop } = useChat({
    id: 'gainsignal-assistant',
    transport,
    experimental_throttle: 80,
  });

  const updateSession = React.useCallback((id: string | null) => {
    sessionRef.current = id;
    setSessionId(id);
  }, []);

  const send = React.useCallback(
    async (text: string) => {
      let active = sessionRef.current;
      if (!active) {
        const response = await assistantApi.createSession(projectId);
        active = response.session.id;
        updateSession(active);
      }
      await sendMessage({ text });
      void queryClient.invalidateQueries({
        queryKey: ['assistant', 'sessions'],
      });
    },
    [projectId, queryClient, sendMessage, updateSession],
  );

  const selectSession = React.useCallback(
    async (id: string) => {
      stop();
      setIsLoadingHistory(true);
      try {
        const response = await assistantApi.getSessionMessages(id);
        updateSession(id);
        setMessages(
          response.messages.map((message) => ({
            id: message.id,
            role: message.role,
            parts: message.parts as UIMessage['parts'],
          })),
        );
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [setMessages, stop, updateSession],
  );

  const newChat = React.useCallback(() => {
    stop();
    updateSession(null);
    setMessages([]);
  }, [setMessages, stop, updateSession]);

  const previousProjectRef = React.useRef(projectId);
  React.useEffect(() => {
    if (previousProjectRef.current !== projectId) {
      previousProjectRef.current = projectId;
      newChat();
    }
  }, [newChat, projectId]);

  const deleteSession = React.useCallback(
    async (id: string) => {
      await assistantApi.deleteSession(id);
      if (sessionRef.current === id) {
        newChat();
      }
      await queryClient.invalidateQueries({
        queryKey: ['assistant', 'sessions'],
      });
    },
    [newChat, queryClient],
  );

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        setIsOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = React.useMemo<AssistantContextValue>(
    () => ({
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((current) => !current),
      messages,
      status,
      error,
      sessionId,
      sessions:
        sessionsQuery.data?.sessions.filter(
          (session) => session.projectId === (projectId ?? null),
        ) ?? [],
      isLoadingHistory,
      send,
      stop,
      newChat,
      selectSession,
      deleteSession,
      pageContext: resolvedPageContext,
      setPageContext,
    }),
    [
      deleteSession,
      error,
      isLoadingHistory,
      isOpen,
      messages,
      newChat,
      projectId,
      resolvedPageContext,
      selectSession,
      send,
      sessionId,
      sessionsQuery.data?.sessions,
      status,
      stop,
    ],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};
